import React, { useState, useEffect } from "react";
import Sidebar from "../../SuperAdmin/Sidebar";
import Header from "../../SuperAdmin/Header";
import { motion, AnimatePresence } from "framer-motion";
import {
    HiOutlineAcademicCap,
    HiOutlineBookOpen,
    HiOutlineClipboardList,
    HiOutlineTrash,
    HiOutlineEye,
    HiOutlineChevronRight,
    HiOutlineX,
    HiOutlineOfficeBuilding,
    HiOutlineCalendar,
    HiOutlineDocumentText,
    HiOutlinePhotograph,
    HiOutlineInformationCircle
} from "react-icons/hi";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

const Courses = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    // State
    const [activeTab, setActiveTab] = useState("undergraduate");
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [courseDetails, setCourseDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const authToken = Cookies.get("superAdminToken");

    // Tab configuration
    const tabs = [
        { id: "undergraduate", label: "Undergraduate", icon: HiOutlineAcademicCap, color: "from-gray-700 to-gray-800" },
        { id: "postgraduate", label: "Postgraduate", icon: HiOutlineBookOpen, color: "from-gray-700 to-gray-800" },
        { id: "admission", label: "Admission", icon: HiOutlineClipboardList, color: "from-gray-700 to-gray-800" },
    ];

    // Fetch courses based on active tab
    useEffect(() => {
        fetchCourses();
    }, [activeTab]);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            let endpoint = "";
            if (activeTab === "undergraduate") {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/undergraduate`;
            } else if (activeTab === "postgraduate") {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/postgraduate`;
            } else {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/admission`;
            }

            const response = await fetch(endpoint, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            const data = await response.json();
            if (data.success) {
                setCourses(data.data || []);
            } else {
                toast.error("Failed to fetch courses");
            }
        } catch (error) {
            console.error("Error fetching courses:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // Fetch course details with menus and descriptions
    const fetchCourseDetails = async (course) => {
        setLoadingDetails(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/${activeTab}/${course._id}`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            const data = await response.json();
            if (data.success) {
                setCourseDetails(data.data);
                setSelectedCourse(course);
                setShowDetailsModal(true);
            } else {
                toast.error("Failed to fetch course details");
            }
        } catch (error) {
            console.error("Error fetching course details:", error);
            toast.error("Something went wrong");
        } finally {
            setLoadingDetails(false);
        }
    };

    // Delete course with cascade
    const handleDelete = async () => {
        if (!courseToDelete) return;

        setDeleting(true);
        try {
            let endpoint = "";
            if (activeTab === "undergraduate") {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/undergraduate/${courseToDelete._id}`;
            } else if (activeTab === "postgraduate") {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/postgraduate/${courseToDelete._id}`;
            } else {
                endpoint = `${process.env.REACT_APP_BACKEND_URL}/apis/superadmin/courses/admission/${courseToDelete._id}`;
            }

            const response = await fetch(endpoint, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            const data = await response.json();
            if (data.success) {
                toast.success(
                    <div>
                        <p className="font-medium">Course Deleted Successfully</p>
                        <p className="text-sm opacity-90">{courseToDelete.courseName || courseToDelete.admissionName}</p>
                        <p className="text-xs opacity-75 mt-1">{data.data?.menusDeleted} menus deleted</p>
                    </div>,
                    { duration: 4000 }
                );
                setCourses(prev => prev.filter(c => c._id !== courseToDelete._id));
                setShowDeleteModal(false);
                setCourseToDelete(null);
            } else {
                toast.error("Failed to delete course");
            }
        } catch (error) {
            console.error("Error deleting course:", error);
            toast.error("Something went wrong");
        } finally {
            setDeleting(false);
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.4 } }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 20 },
        visible: { 
            opacity: 1, 
            scale: 1, 
            y: 0,
            transition: { type: "spring", damping: 25, stiffness: 300 }
        },
        exit: { 
            opacity: 0, 
            scale: 0.9, 
            y: 20,
            transition: { duration: 0.2 }
        }
    };

    // Skeleton Loader
    const SkeletonCard = () => (
        <div className="bg-white rounded-xl p-5 border border-gray-200 animate-pulse">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="w-16 h-8 bg-gray-200 rounded"></div>
            </div>
        </div>
    );

    // Empty State
    const EmptyState = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="col-span-full text-center py-16 bg-white rounded-xl border border-gray-200"
        >
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <HiOutlineInformationCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Courses Found</h3>
            <p className="text-gray-500 text-sm">No {tabs.find(t => t.id === activeTab)?.label} courses available.</p>
        </motion.div>
    );

    const TabIcon = tabs.find(t => t.id === activeTab)?.icon;

    return (
        <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

            <div className="flex-1 flex flex-col overflow-hidden">
                <Header title="Course Management" toggleSidebar={toggleSidebar} />

                {/* Main Content Area - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
                        {/* Header Section */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-6"
                        >
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Course Management</h1>
                            <p className="text-sm text-gray-500">View and manage all courses across colleges</p>
                        </motion.div>

                        {/* Tabs */}
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide"
                        >
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                                            activeTab === tab.id
                                                ? `bg-gradient-to-r ${tab.color} text-white shadow-md`
                                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </motion.div>

                        {/* Stats Summary */}
                        {!loading && courses.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl p-4 mb-6 border border-gray-200"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <TabIcon className="w-5 h-5 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Total {tabs.find(t => t.id === activeTab)?.label} Courses</p>
                                        <p className="text-2xl font-bold text-gray-800">{courses.length}</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Course Grid */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                        >
                            {loading ? (
                                [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                            ) : courses.length === 0 ? (
                                <EmptyState />
                            ) : (
                                courses.map((course, index) => (
                                    <motion.div
                                        key={course._id}
                                        variants={itemVariants}
                                        custom={index}
                                        whileHover={{ y: -4 }}
                                        className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all group"
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800 text-lg mb-1 line-clamp-1">
                                                    {course.courseName || course.admissionName}
                                                </h3>
                                                <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                                                    <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400" />
                                                    <span className="truncate">{course.collegeName}</span>
                                                </div>
                                                {course.collegeEmail && (
                                                    <p className="text-xs text-gray-400 truncate">{course.collegeEmail}</p>
                                                )}
                                            </div>
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => fetchCourseDetails(course)}
                                                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                                    title="View Details"
                                                >
                                                    <HiOutlineEye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setCourseToDelete(course);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Course"
                                                >
                                                    <HiOutlineTrash className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-between items-center mt-2 pt-3 border-t border-gray-100">
                                            <span className="text-xs text-gray-400">
                                                {new Date(course.createdAt).toLocaleDateString()}
                                            </span>
                                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                                {course.exploreCourse || tabs.find(t => t.id === activeTab)?.label}
                                            </span>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Course Details Modal */}
            <AnimatePresence>
                {showDetailsModal && courseDetails && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <HiOutlineAcademicCap className="w-5 h-5" />
                                    Course Details
                                </h2>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6">
                                {loadingDetails ? (
                                    <div className="flex justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-gray-600"></div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Course Info */}
                                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 mb-6">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                                {courseDetails.course.name}
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="flex items-center gap-2">
                                                    <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
                                                    <span className="text-sm text-gray-600">{courseDetails.course.collegeName}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <HiOutlineCalendar className="w-4 h-4 text-gray-500" />
                                                    <span className="text-sm text-gray-600">
                                                        Created: {new Date(courseDetails.course.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="grid grid-cols-2 gap-4 mb-6">
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <p className="text-sm text-gray-500">Total Menus</p>
                                                <p className="text-2xl font-bold text-gray-800">{courseDetails.totalMenus}</p>
                                            </div>
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <p className="text-sm text-gray-500">Total Descriptions</p>
                                                <p className="text-2xl font-bold text-gray-800">{courseDetails.totalDescriptions}</p>
                                            </div>
                                        </div>

                                        {/* Menus and Descriptions */}
                                        {courseDetails.menus.length === 0 ? (
                                            <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                                                <HiOutlineDocumentText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                                <p className="text-gray-500">No menus found for this course</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {courseDetails.menus.map((menu) => (
                                                    <div key={menu._id} className="border border-gray-200 rounded-xl overflow-hidden">
                                                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                                            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                                                <HiOutlineDocumentText className="w-4 h-4" />
                                                                {menu.menuName}
                                                            </h4>
                                                        </div>
                                                        
                                                        {menu.descriptions.length === 0 ? (
                                                            <div className="p-4 text-center text-gray-400 text-sm">
                                                                No descriptions for this menu
                                                            </div>
                                                        ) : (
                                                            <div className="p-4 space-y-3">
                                                                {menu.descriptions.map((desc) => (
                                                                    <div key={desc._id} className="bg-gray-50 p-3 rounded-lg">
                                                                        <h5 className="font-medium text-gray-700 text-sm mb-2">{desc.title}</h5>
                                                                        <p className="text-xs text-gray-600 mb-2">{desc.description}</p>
                                                                        {desc.image && (
                                                                            <img
                                                                                src={desc.image}
                                                                                alt={desc.title}
                                                                                className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && courseToDelete && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-2xl shadow-xl max-w-md w-full border border-gray-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                        <HiOutlineTrash className="w-5 h-5 text-red-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800">Delete Course</h3>
                                </div>

                                <p className="text-gray-600 mb-2">
                                    Are you sure you want to delete <span className="font-semibold text-gray-800">
                                        {courseToDelete.courseName || courseToDelete.admissionName}
                                    </span>?
                                </p>
                                
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-amber-700">
                                        ⚠️ This will permanently delete:
                                    </p>
                                    <ul className="text-xs text-amber-600 mt-1 list-disc list-inside">
                                        <li>The course itself</li>
                                        <li>All menus under this course</li>
                                        <li>All descriptions under those menus</li>
                                    </ul>
                                    <p className="text-xs text-amber-600 mt-1">This action cannot be undone.</p>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                                    >
                                        {deleting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Deleting...
                                            </>
                                        ) : (
                                            'Delete Course'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Courses;